import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Cloud, ShieldCheck, Loader2, ExternalLink, CheckCircle2, Copy, ChevronDown, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const NETLIFY_HEADERS = `/*
  Strict-Transport-Security: max-age=31536000; includeSubDomains; preload
  X-Frame-Options: DENY
  X-Content-Type-Options: nosniff
  Referrer-Policy: strict-origin-when-cross-origin
  Permissions-Policy: geolocation=(), microphone=(), camera=()`;

const VERCEL_JSON = `{
  "headers": [
    {
      "source": "/(.*)",
      "headers": [
        { "key": "Strict-Transport-Security", "value": "max-age=31536000; includeSubDomains; preload" },
        { "key": "X-Frame-Options", "value": "DENY" },
        { "key": "X-Content-Type-Options", "value": "nosniff" },
        { "key": "Referrer-Policy", "value": "strict-origin-when-cross-origin" },
        { "key": "Permissions-Policy", "value": "geolocation=(), microphone=(), camera=()" }
      ]
    }
  ]
}`;

function ConfigSnippet({ title, filename, code, testid }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] text-zinc-300 font-semibold">{title} <span className="text-zinc-500 font-mono">{filename}</span></span>
        <button onClick={() => { navigator.clipboard?.writeText(code); toast.success(`${filename} copied`); }}
          data-testid={`${testid}-copy`} className="text-zinc-400 hover:text-white transition-colors" title="Copy">
          <Copy size={13} />
        </button>
      </div>
      <pre className="text-[10.5px] text-[#8fe3ff] font-mono whitespace-pre-wrap break-all leading-relaxed max-h-48 overflow-y-auto">{code}</pre>
    </div>
  );
}

export default function CloudflareApply({ domain }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [subs, setSubs] = useState(true);
  const [preload, setPreload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [showOther, setShowOther] = useState(false);
  const [autoApply, setAutoApply] = useState(false);

  const loadStatus = () => {
    api.get(`/cloudflare/status/${encodeURIComponent(domain)}`).then((r) => {
      setStatus(r.data);
      setAutoApply(Boolean(r.data?.auto_apply));
    }).catch(() => {});
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [domain]);

  const toggleAutoApply = async (next) => {
    setAutoApply(next);
    try {
      await api.patch(`/cloudflare/${encodeURIComponent(domain)}/auto-apply`, { enabled: next });
      toast.success(next ? "Auto-fix on — we'll re-apply headers if they drop." : "Auto-fix turned off.");
    } catch {
      setAutoApply(!next);
      toast.error("Could not update auto-fix.");
    }
  };

  const apply = async () => {
    if (!token.trim()) { toast.error("Paste your Cloudflare API token first."); return; }
    setLoading(true);
    try {
      const res = await api.post("/cloudflare/apply", {
        domain, api_token: token.trim(), include_subdomains: subs, preload,
      });
      const applied = res.data.applied || [];
      const warnings = res.data.warnings || [];
      if (applied.length) toast.success(`Live on ${domain}: ${applied.join(" · ")}`);
      if (warnings.length) {
        toast.warning(warnings[0], { duration: 9000 });
      } else if (!applied.length) {
        toast.error("Cloudflare accepted the request but no headers are live yet — re-scan in a minute.");
      }
      setToken("");
      setOpen(false);
      loadStatus();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not apply Cloudflare settings.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#121212] border border-[#f6821f]/30 rounded-xl p-5 mb-6" data-testid="cloudflare-apply-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Cloud size={16} className="text-[#f6821f]" />
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Apply via Cloudflare</h3>
        </div>
        {status?.connected && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-[#32D74B]" data-testid="cloudflare-applied-status">
            <CheckCircle2 size={13} /> Applied {timeAgo(status.last_applied_at)}
          </span>
        )}
      </div>
      <p className="text-zinc-500 text-sm mt-2 mb-3">
        Push a strong HSTS header and core security headers (X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy) live to this domain's Cloudflare zone — no copy-paste needed.
      </p>
      {status?.connected && Array.isArray(status.applied) && (
        <div className="text-[11px] text-[#32D74B] mb-3 space-y-0.5" data-testid="cloudflare-applied-list">
          {status.applied.map((a, i) => <div key={i}>✓ {a}</div>)}
        </div>
      )}
      {status?.connected && Array.isArray(status.warnings) && status.warnings.length > 0 && (
        <div className="text-[11px] text-amber-400/90 mb-3 space-y-1 rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5" data-testid="cloudflare-warnings-list">
          {status.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}
      <button onClick={() => setOpen(true)} data-testid="cloudflare-apply-open-btn"
        className="inline-flex items-center gap-2 btn-brand text-white rounded-lg px-4 py-2 text-sm font-medium">
        <ShieldCheck size={15} /> {status?.connected ? "Re-apply security headers" : "Apply security headers via Cloudflare"}
      </button>

      {status?.connected && (
        <label className="flex items-center gap-2.5 mt-3 rounded-lg border border-white/10 bg-black/20 p-3 cursor-pointer" data-testid="cloudflare-auto-apply-toggle">
          <input type="checkbox" checked={autoApply} onChange={(e) => toggleAutoApply(e.target.checked)} className="accent-[#f6821f] mt-0.5" data-testid="cloudflare-auto-apply-checkbox" />
          <span className="text-sm text-zinc-300">
            <span className="inline-flex items-center gap-1.5 font-medium text-white"><Zap size={13} className="text-[#f6821f]" /> Auto-fix if a header drops</span>
            <span className="block text-[11px] text-zinc-500 mt-0.5">If daily monitoring finds one of these headers removed, we re-apply it via Cloudflare automatically and email you a "we fixed it" note. (Pro)</span>
          </span>
        </label>
      )}

      <button onClick={() => setShowOther((s) => !s)} data-testid="cloudflare-other-cdns-toggle"
        className="inline-flex items-center gap-1.5 mt-3 text-xs text-zinc-400 hover:text-white transition-colors">
        Not on Cloudflare? Netlify &amp; Vercel config <ChevronDown size={13} className={`transition-transform ${showOther ? "rotate-180" : ""}`} />
      </button>
      {showOther && (
        <div className="mt-3 space-y-3" data-testid="cloudflare-other-cdns">
          <p className="text-[11px] text-zinc-500">Netlify &amp; Vercel set headers at deploy time (no live API), so commit one of these to your repo and redeploy — the exact same headers as the Cloudflare apply. <span className="text-zinc-400">With Pro, if monitoring later finds one of these headers dropped, we email you this exact config so you can redeploy and restore it.</span></p>
          <ConfigSnippet title="Netlify" filename="_headers" code={NETLIFY_HEADERS} testid="netlify-snippet" />
          <ConfigSnippet title="Vercel" filename="vercel.json" code={VERCEL_JSON} testid="vercel-snippet" />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#161616] border-white/10 text-white max-w-md" data-testid="cloudflare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cloud size={16} className="text-[#f6821f]" /> Apply via Cloudflare</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Paste a Cloudflare API Token scoped to <b>{domain}</b> with <b>Zone:Read</b>, <b>Zone Settings:Edit</b>, and <b>Transform Rules:Edit</b>.
            </DialogDescription>
          </DialogHeader>

          <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[#4da3ff] text-xs hover:underline" data-testid="cloudflare-token-help">
            Create a token in Cloudflare <ExternalLink size={12} />
          </a>

          <label className="text-xs text-zinc-400 mt-1">Cloudflare API Token</label>
          <input
            type="password" value={token} onChange={(e) => setToken(e.target.value)}
            placeholder="••••••••••••••••••••••••" data-testid="cloudflare-token-input"
            className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#f6821f]/60"
          />

          <div className="space-y-2 mt-1">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="cloudflare-subs-toggle">
              <input type="checkbox" checked={subs} onChange={(e) => setSubs(e.target.checked)} className="accent-[#f6821f]" />
              Include subdomains
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="cloudflare-preload-toggle">
              <input type="checkbox" checked={preload} onChange={(e) => setPreload(e.target.checked)} className="accent-[#f6821f]" />
              Enable preload <span className="text-[11px] text-amber-400/80">(one-way commitment — only if all subdomains have HTTPS)</span>
            </label>
          </div>

          <p className="text-[11px] text-zinc-500">Your token is encrypted at rest and never shown again. It only touches this zone's HSTS + security-header settings; existing Transform Rules are preserved.</p>

          <DialogFooter>
            <button onClick={apply} disabled={loading} data-testid="cloudflare-apply-confirm-btn"
              className="inline-flex items-center gap-2 btn-brand text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60 w-full justify-center">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
              {loading ? "Applying…" : "Apply now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
